import { Set } from 'immutable';
import createCachedSelector from 're-reselect';

import {
  selectJobIdUserIdMap,
  selectJobIdWageRateMap,
  selectShiftIdJobIdMap,
} from 'features/scheduleBuilder/ScheduleBuilderView/selectors/index';
import { selectRoleLaborTotalsByRoleId } from 'features/scheduleBuilder/slices/roles';
import { selectJobIdsForUser } from 'features/scheduleBuilder/slices/users/slice';

import { toCurrency } from 'util/currency';
import { datesInRange } from 'util/dateTime';

import { MAX_HOURS_IN_DAY_VIEW } from '../DayView/constants';
import RoleLaborTotals from '../resources/RoleLaborTotals';
import { getDecimalTimeRange } from '../utils/dayViewData';
import { updateOvernightTotals } from '../utils/roleLaborTotals';

import { selectOvernightShiftsForDay } from './dayViewData';
import {
  selectCurrentDateRangeEnd,
  selectCurrentDateRangeStart,
} from './index';

const selectDayLaborTotalForJobId = createCachedSelector(
  (state, props) =>
    RoleLaborTotals.selectWhereAttribute(state, 'date', props.date),
  (_, props) => props.jobId,
  (totals, jobId) =>
    totals.filter(
      roleLaborTotal => roleLaborTotal.relationships.job.data.id === jobId
    )
)((_, props) => `selectDayLaborTotalForJobId-${props.jobId}-${props.date}`);

const selectDayLaborTotalForUserId = createCachedSelector(
  (state, props) =>
    RoleLaborTotals.selectWhereAttribute(state, 'date', props.date),
  selectJobIdsForUser,
  (totals, jobIds) =>
    totals.filter(roleLaborTotal =>
      jobIds.includes(roleLaborTotal.relationships.job.data.id)
    )
)((_, props) => `selectDayLaborTotalForJobId-${props.jobId}-${props.date}`);

const selectLaborTotalsByRoleId = createCachedSelector(
  (state, { roleId }) => roleId,
  (state, { roleId }) => {
    if (roleId === 'uncategorized') {
      return [];
    }
    return selectRoleLaborTotalsByRoleId(state, { roleId }).map(
      total => total.id
    );
  },
  RoleLaborTotals.selectAllEntities,
  (roleId, roleLaborTotalIds, roleLaborTotals) => {
    if (roleId === 'uncategorized') {
      return roleLaborTotals.filter(
        laborTotal => !laborTotal.relationships.role.data
      );
    }
    return roleLaborTotals.filter(laborTotal =>
      roleLaborTotalIds.includes(laborTotal.id)
    );
  }
)((_, { roleId }) => `selectLaborTotalsByRoleId-${roleId}`);

const selectRoleLaborTotalsByJobId = createCachedSelector(
  RoleLaborTotals.selectAllEntities,
  (_, props) => props.jobId,
  selectCurrentDateRangeStart,
  selectCurrentDateRangeEnd,
  (roleLaborTotals, jobId, startDate, endDate) => {
    const range = datesInRange(startDate, endDate);

    return roleLaborTotals.filter(
      roleLaborTotal =>
        roleLaborTotal.relationships.job.data.id === jobId &&
        range.includes(roleLaborTotal.attributes.date)
    );
  }
)((_, props) => `selectRoleLaborTotalsByJobId-${props.jobId}`);

const selectRoleLaborTotalsByUserId = createCachedSelector(
  RoleLaborTotals.selectAllEntities,
  selectJobIdsForUser,
  selectCurrentDateRangeStart,
  selectCurrentDateRangeEnd,
  (roleLaborTotals, jobIds, startDate, endDate) => {
    const range = datesInRange(startDate, endDate);

    return roleLaborTotals.filter(
      roleLaborTotal =>
        jobIds.includes(roleLaborTotal.relationships.job.data.id) &&
        range.includes(roleLaborTotal.attributes.date)
    );
  }
)((_, props) => `selectRoleLaborTotalsByUserId-${props.jobId}`);

export const selectHourTotalsByJobId = createCachedSelector(
  (state, props) =>
    RoleLaborTotals.selectWhereAttribute(state, 'date', props.date),
  totals => {
    const totalsByJobId = {};
    totals.forEach(total => {
      const jobId = total.relationships.job.data.id;
      totalsByJobId[jobId] = total.attributes.hourTotals;
    });
    return totalsByJobId;
  }
)((_, props) => `selectHourTotalsByJobId-${props.date}`);

export const selectOvernightRangeByJobId = createCachedSelector(
  selectOvernightShiftsForDay,
  selectShiftIdJobIdMap,
  (overnightShifts, shiftIdJobIdMap) => {
    const overnightRangeByJobId = {};

    overnightShifts.forEach(shift => {
      const jobId = shiftIdJobIdMap[shift.id];

      overnightRangeByJobId[jobId] = getDecimalTimeRange({
        start: shift.attributes.startAt,
        end: shift.attributes.endAt,
        overnight: true,
      });
    });
    return overnightRangeByJobId;
  }
)((_, props) => `dayView-selectOvernightRangeByJobId-${props.date}`);

export const selectOvernightHourTotalsByJob = createCachedSelector(
  selectOvernightRangeByJobId,
  selectHourTotalsByJobId,
  selectJobIdWageRateMap,
  (overnightRangeByJobId, totalsByJobId, jobIdWageRate) => {
    const updatedTotalsByJobId = {};

    Object.entries(overnightRangeByJobId).forEach(
      ([jobId, overnightTimeRange]) => {
        let overnightTotals = {};
        if (overnightTimeRange) {
          overnightTotals = updateOvernightTotals({
            overnightTimeRange,
            overnightTotals,
            totals: totalsByJobId[jobId],
            wageRate: jobIdWageRate[jobId],
          });
        }

        updatedTotalsByJobId[jobId] = overnightTotals;
      }
    );

    return updatedTotalsByJobId;
  }
)((_, props) => `dayView-selectOvernightHourTotalsByJob-${props.date}`);

const selectRoleLaborTotalsForCurrentLocation = createCachedSelector(
  selectJobIdUserIdMap,
  (state, props) =>
    RoleLaborTotals.selectWhereAttribute(state, 'date', props.date),
  (jobIdUserIdMap, laborTotals) =>
    laborTotals.filter(l =>
      Object.keys(jobIdUserIdMap).includes(l?.relationships?.job?.data?.id)
    )
)((_, props) => `selectRoleLaborTotalsForCurrentLocation-${props.date}`);

export const selectHourTotals = createCachedSelector(
  selectOvernightHourTotalsByJob,
  selectRoleLaborTotalsForCurrentLocation,
  (overnightHourTotals, totals) =>
    [...Array(MAX_HOURS_IN_DAY_VIEW).keys()].reduce((memo, hour) => {
      memo[hour] ||= { hours: 0, wages: 0, employees: Set([]) };

      totals.forEach(total => {
        const totalJobId = total.relationships.job.data.id;
        let hourTotals = total.attributes.hourTotals;

        const overnightTotalExists =
          Object.keys(overnightHourTotals).includes(totalJobId);

        if (overnightTotalExists) {
          hourTotals = overnightHourTotals[totalJobId];
        }

        memo[hour].hours += hourTotals[hour]?.hours || 0;
        memo[hour].wages += hourTotals[hour]?.wages || 0;

        if (hourTotals[hour]?.employees) {
          memo[hour].employees = memo[hour].employees.union(
            hourTotals[hour].employees
          );
        }
      });

      return memo;
    }, [])
)((_, props) => `selectHourTotals-${props.date}`);

export const selectHourHours = createCachedSelector(
  selectHourTotals,
  (_, props) => props.hour,
  (totals, hour) => `${totals[hour].hours.toFixed(2)} hrs`
)((_, props) => `selectHourHours-${props.date}`);

export const selectHoursWages = createCachedSelector(
  selectHourTotals,
  (_, props) => props.hour,
  (totals, hour) => toCurrency(totals[hour].wages)
)((_, props) => `selectHoursWages-${props.date}`);

export const selectHourEmployees = createCachedSelector(
  selectHourTotals,
  (_, props) => props.hour,
  (totals, hour) => totals[hour].employees.size
)((_, props) => `selectHourEmployees-${props.date}`);

export const selectSummaryHours = createCachedSelector(
  selectHourTotals,
  totals =>
    `${totals.reduce((memo, total) => memo + total.hours, 0).toFixed(2)} hrs`
)((_, props) => `selectSummaryHours-${props.date}`);

export const selectSummarysWages = createCachedSelector(
  selectHourTotals,
  totals => toCurrency(totals.reduce((memo, total) => memo + total.wages, 0))
)((_, props) => `selectSummarysWages-${props.date}`);

export const selectSummaryEmployees = createCachedSelector(
  selectHourTotals,
  totals =>
    totals.reduce((memo, total) => memo.union(total.employees), Set()).size
)((_, props) => `selectSummaryEmployees-${props.date}`);

export const selectDateWages = createCachedSelector(
  selectRoleLaborTotalsForCurrentLocation,

  laborTotals =>
    laborTotals?.reduce(
      (memo, totals) => memo + (totals.attributes.wages || 0),
      0
    ) || 0
)((_, props) => `selectDateWages-${props.date}`);

export const selectDateHours = createCachedSelector(
  selectRoleLaborTotalsForCurrentLocation,
  laborTotals =>
    laborTotals?.reduce((memo, totals) => memo + totals.attributes.hours, 0) ||
    0
)((_, props) => `selectDateHours-${props.date}`);

export const selectDayHoursForJob = createCachedSelector(
  selectDayLaborTotalForJobId,
  totals =>
    totals.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.attributes.hours || 0),
      0
    )
)((_, props) => `selectDayHoursForJob-${props.jobId}-${props.date}`);

export const selectDayWagesForJob = createCachedSelector(
  selectDayLaborTotalForJobId,
  totals =>
    totals.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.attributes.wages || 0),
      0
    )
)((_, props) => `selectDayWagesForJob-${props.jobId}-${props.date}`);

export const selectDayHoursForUser = createCachedSelector(
  selectDayLaborTotalForUserId,
  totals =>
    totals.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.attributes.hours || 0),
      0
    )
)((_, props) => `selectDayHoursForJob-${props.jobId}-${props.date}`);

export const selectWagesForWeekByJobId = createCachedSelector(
  selectRoleLaborTotalsByJobId,
  roleLaborTotals =>
    roleLaborTotals?.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.attributes.wages || 0),
      0
    )
)((_, props) => `selectWagesForWeekByJobId-${props.jobId}`);

export const selectHoursForWeekByJobId = createCachedSelector(
  selectRoleLaborTotalsByJobId,
  roleLaborTotals =>
    roleLaborTotals?.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.attributes.hours || 0),
      0
    )
)((_, props) => `selectHoursForWeekByJobId-${props.jobId}`);

export const selectHoursForWeekByUserId = createCachedSelector(
  selectRoleLaborTotalsByUserId,
  roleLaborTotals =>
    roleLaborTotals?.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.attributes.hours || 0),
      0
    )
)((_, props) => `selectHoursForWeekByUserId-${props.userId}`);

export const selectHasOvertimeForWeekByJobId = createCachedSelector(
  selectRoleLaborTotalsByJobId,
  roleLaborTotals =>
    roleLaborTotals?.some(
      roleLaborTotal => roleLaborTotal.attributes.hasOvertime
    )
)((_, props) => `selectHasOvertimeForWeekByJobId-${props.jobId}`);

export const selectHasOvertimeForWeekByUserId = createCachedSelector(
  selectRoleLaborTotalsByUserId,
  roleLaborTotals =>
    roleLaborTotals?.some(
      roleLaborTotal => roleLaborTotal.attributes.hasOvertime
    )
)((_, props) => `selectHasOvertimeForWeekByUserId-${props.userId}`);

export const selectDayTotalHoursByRoleId = createCachedSelector(
  selectLaborTotalsByRoleId,
  laborTotals =>
    laborTotals.reduce((memo, labourTotal) => {
      memo += labourTotal.attributes.hours || 0;
      return memo;
    }, 0)
)((_, { roleId }) => `selectDayTotalHoursByRoleId-${roleId}`);

export const selectDayTotalWagesByRoleId = createCachedSelector(
  selectLaborTotalsByRoleId,
  laborTotals =>
    laborTotals.reduce((memo, labourTotal) => {
      memo += labourTotal.attributes.wages || 0;
      return memo;
    }, 0)
)((_, { roleId }) => `selectDayTotalWagesByRoleId-${roleId}`);
